import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as clientsApi from '../../api/clients';
import * as employeesApi from '../../api/employees';

const emptyForm = { clientName: '', companyName: '', contactPerson: '', email: '', password: '', phone: '', address: '', taxId: '', notes: '' };

export default function Clients() {
  const { actor } = useAuth();
  const canManage = actor.role === 'super_admin' || actor.role === 'admin';

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const [assignTarget, setAssignTarget] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    clientsApi
      .listClients({ page, search })
      .then((res) => {
        setRows(res.data);
        setPagination(res.pagination);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load clients.'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({
      clientName: row.clientName,
      companyName: row.companyName || '',
      contactPerson: row.contactPerson || '',
      address: row.address || '',
      taxId: row.taxId || '',
      notes: row.notes || '',
    });
    setEditingId(row.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await clientsApi.updateClient(editingId, form);
        setSuccess('Client updated.');
      } else {
        await clientsApi.createClient(form);
        setSuccess('Client account created.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save client.');
    }
  };

  const handleStatusChange = async (row, status) => {
    try {
      await clientsApi.setClientStatus(row.id, status);
      setSuccess(`${row.clientName} marked as ${status}.`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status.');
    }
  };

  const openAssign = async (row) => {
    setAssignTarget(row);
    if (allEmployees.length === 0) {
      const res = await employeesApi.listEmployees({ limit: 500 });
      setAllEmployees(res.data);
    }
    const detail = await clientsApi.getClient(row.id);
    setSelectedEmployeeIds(detail.data.assignedEmployees.map((e) => e.id));
  };

  const toggleEmployeeSelection = (id) => {
    setSelectedEmployeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const saveAssignment = async () => {
    try {
      await clientsApi.setClientAssignedEmployees(assignTarget.id, selectedEmployeeIds);
      setSuccess(`Employee assignments updated for ${assignTarget.clientName}.`);
      setAssignTarget(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update assignments.');
    }
  };

  const columns = [
    { key: 'clientName', label: 'Client Name', render: (r) => <Link to={`/clients/${r.id}`}>{r.clientName}</Link> },
    { key: 'companyName', label: 'Company', render: (r) => r.companyName || '-' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone', render: (r) => r.phone || '-' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="btn-group">
          <Link to={`/clients/${r.id}`} className="btn-link">History</Link>
          {canManage && (
            <>
              <button type="button" className="btn-link" onClick={() => openEdit(r)}>Edit</button>
              <button type="button" className="btn-link" onClick={() => openAssign(r)}>Assign Employees</button>
              {r.status === 'active' ? (
                <button type="button" className="btn-link" onClick={() => handleStatusChange(r, 'archived')}>Archive</button>
              ) : (
                <button type="button" className="btn-link" onClick={() => handleStatusChange(r, 'active')}>Reactivate</button>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout title="Clients">
      <div className="page-header">
        <div>
          <h1 className="heading">Clients</h1>
          <p className="page-subtext">There is no limit on the number of clients the firm can manage here.</p>
        </div>
        {canManage && <button type="button" className="btn" onClick={openCreate}>Add Client</button>}
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      <div className="filter-bar">
        <input type="text" placeholder="Search by name, company or email" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
      </div>

      {loading ? (
        <Loader text="Loading clients..." />
      ) : (
        <>
          <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No clients found." />
          <Pagination pagination={pagination} onPageChange={setPage} />
        </>
      )}

      {showForm && (
        <Modal
          title={editingId ? 'Edit Client' : 'Add Client'}
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="client-form" className="btn">Save</button>
            </>
          }
        >
          <form id="client-form" onSubmit={handleSave}>
            <div className="form-grid single-column">
              <FormField label="Client Name" value={form.clientName} onChange={(e) => setForm({ ...form, clientName: e.target.value })} required />
              <FormField label="Company Name" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
              <FormField label="Contact Person" value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} />
              {!editingId && (
                <>
                  <FormField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                  <FormField label="Initial Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
                  <FormField label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </>
              )}
              <FormField label="Address" type="textarea" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              <FormField label="Tax ID" value={form.taxId} onChange={(e) => setForm({ ...form, taxId: e.target.value })} />
              <FormField label="Notes" type="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </form>
        </Modal>
      )}

      {assignTarget && (
        <Modal
          title={`Assign Employees - ${assignTarget.clientName}`}
          onClose={() => setAssignTarget(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setAssignTarget(null)}>Cancel</button>
              <button type="button" className="btn" onClick={saveAssignment}>Save Assignment</button>
            </>
          }
        >
          <p className="page-subtext">Select every employee who should service this client. There is no limit on how many employees can be assigned.</p>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {allEmployees.map((emp) => (
              <label key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <input
                  type="checkbox"
                  checked={selectedEmployeeIds.includes(emp.id)}
                  onChange={() => toggleEmployeeSelection(emp.id)}
                  style={{ width: 'auto' }}
                />
                {emp.name} {emp.designation ? `(${emp.designation})` : ''}
              </label>
            ))}
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
